import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import type {
  PlayRoomGameResponse,
  RoomGamePrizeSummary,
  RoomGameType,
  SlotMatchKind,
  SlotSymbol,
} from '@fetmode/types';
import { SLOT_SYMBOLS } from '@fetmode/types';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import './RoomGameOverlays.css';

const WHEEL_COLORS = [
  '#4A148C',
  '#FF4081',
  '#00E5FF',
  '#7C4DFF',
  '#FF80AB',
  '#18FFFF',
  '#B388FF',
  '#F50057',
] as const;

const SPIN_DURATION_MS = 4500;
const SPIN_MIN_TURNS = 6;

function wheelGradient(count: number): string {
  if (count <= 0) {
    return 'conic-gradient(#4A148C 0deg 360deg)';
  }
  const slice = 360 / count;
  const stops: string[] = [];
  for (let i = 0; i < count; i += 1) {
    const color = WHEEL_COLORS[i % WHEEL_COLORS.length] ?? '#4A148C';
    const start = i * slice;
    const end = (i + 1) * slice;
    stops.push(`${color} ${String(start)}deg ${String(end)}deg`);
  }
  // 0deg is 12 o'clock — same frame as label angles and the top pointer.
  return `conic-gradient(from 0deg, ${stops.join(', ')})`;
}

/** Clockwise degrees from top to the center of slice `index`. */
function sliceMidAngleDeg(index: number, count: number): number {
  const slice = 360 / Math.max(1, count);
  return index * slice + slice / 2;
}

export interface SpinWheelOverlayProps {
  costCoins: number;
  prizes: RoomGamePrizeSummary[];
  busy: boolean;
  result: PlayRoomGameResponse | null;
  error: string | null;
  onPlay: () => void;
  onClose: () => void;
}

export function SpinWheelOverlay({
  costCoins,
  prizes,
  busy,
  result,
  error,
  onPlay,
  onClose,
}: SpinWheelOverlayProps): ReactElement {
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [showWin, setShowWin] = useState(false);
  const rotationRef = useRef(0);
  const segmentCount = Math.max(1, prizes.length);

  useEffect(() => {
    if (!result || result.gameType !== 'WHEEL') {
      return;
    }

    const count = Math.max(1, result.segmentCount);
    // Midpoint of the winning slice (clockwise from top) — must match label + gradient frame.
    const prizeMid = sliceMidAngleDeg(result.prize.index, count);
    // Rotate clockwise so that prize mid lands under the top pointer.
    const landOffset = (360 - prizeMid) % 360;
    const current = rotationRef.current;
    const currentMod = ((current % 360) + 360) % 360;
    const deltaToLand = (landOffset - currentMod + 360) % 360;
    const next = current + SPIN_MIN_TURNS * 360 + deltaToLand;

    setShowWin(false);
    setSpinning(true);

    let innerFrame = 0;
    // Ensure the browser commits the current transform before starting the transition.
    const outerFrame = window.requestAnimationFrame(() => {
      innerFrame = window.requestAnimationFrame(() => {
        rotationRef.current = next;
        setRotation(next);
      });
    });

    const timer = window.setTimeout(() => {
      setSpinning(false);
      setShowWin(true);
    }, SPIN_DURATION_MS);

    return () => {
      window.cancelAnimationFrame(outerFrame);
      window.cancelAnimationFrame(innerFrame);
      window.clearTimeout(timer);
    };
  }, [result]);

  return (
    <div className="room-game-overlay" role="dialog" aria-modal="true" aria-labelledby="spin-wheel-title">
      <div className="room-game-overlay__panel">
        <header className="room-game-overlay__header">
          <h2 id="spin-wheel-title">{t('common.show.spinWheelTitle')}</h2>
          <button type="button" className="room-game-overlay__close" onClick={onClose}>
            {t('common.show.gamesMenuClose')}
          </button>
        </header>
        <p className="room-game-overlay__hint">{t('common.show.spinWheelHint')}</p>

        <div className="spin-wheel">
          <div className="spin-wheel__pointer" aria-hidden="true" />
          <div
            className={spinning ? 'spin-wheel__disc spin-wheel__disc--spinning' : 'spin-wheel__disc'}
            style={{
              background: wheelGradient(segmentCount),
              transform: `rotate(${String(rotation)}deg)`,
              transitionDuration: spinning ? `${String(SPIN_DURATION_MS)}ms` : '0ms',
            }}
          >
            {prizes.map((prize, index) => {
              const midAngle = sliceMidAngleDeg(index, segmentCount);
              return (
                <span
                  key={prize.id}
                  className="spin-wheel__label"
                  style={{
                    // Center of label sits on the slice bisector; final rotate reads text outward.
                    transform: `translate(-50%, -50%) rotate(${String(midAngle)}deg) translateY(var(--spin-wheel-label-offset, -4.25rem)) rotate(90deg)`,
                  }}
                >
                  {prize.label}
                </span>
              );
            })}
          </div>
        </div>

        {showWin && result ? (
          <p className="room-game-overlay__result" role="status">
            {t('common.show.gameWin').replace('{{prize}}', result.prize.label)}
          </p>
        ) : null}
        {error ? <p className="fm-error">{error}</p> : null}

        <div className="room-game-overlay__actions">
          <Button
            label={t('common.show.spinWheelPlay').replace('{{cost}}', String(costCoins))}
            disabled={busy || spinning || prizes.length === 0}
            onClick={onPlay}
          />
        </div>

        <ul className="room-game-overlay__odds">
          {prizes.map((prize) => (
            <li key={prize.id}>
              <span>{prize.label}</span>
              <span>{`${String(Math.round(prize.odds * 100))}%`}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export interface SlotMachineOverlayProps {
  costCoins: number;
  prizes: RoomGamePrizeSummary[];
  busy: boolean;
  result: PlayRoomGameResponse | null;
  error: string | null;
  onPlay: () => void;
  onClose: () => void;
}

const SLOT_STOP_DELAYS_MS = [1000, 1800, 2700] as const;

const SLOT_PAYTABLE: ReadonlyArray<{ kind: SlotMatchKind; labelKey: string }> = [
  { kind: 'JACKPOT', labelKey: 'common.show.slotMatchJackpot' },
  { kind: 'THREE', labelKey: 'common.show.slotMatchTriple' },
  { kind: 'PAIR', labelKey: 'common.show.slotMatchPair' },
  { kind: 'NONE', labelKey: 'common.show.slotMatchNone' },
];

const SLOT_MATCH_WEIGHTS: Record<SlotMatchKind, number> = {
  NONE: 42,
  PAIR: 32,
  THREE: 20,
  JACKPOT: 6,
};

function slotMatchOddsPercent(kind: SlotMatchKind): number {
  const total = Object.values(SLOT_MATCH_WEIGHTS).reduce((sum, weight) => sum + weight, 0);
  return Math.round((SLOT_MATCH_WEIGHTS[kind] / total) * 100);
}

function SlotReel({
  finalSymbol,
  delayMs,
  spinKey,
}: {
  finalSymbol: SlotSymbol;
  delayMs: number;
  spinKey: string;
}): ReactElement {
  const [symbol, setSymbol] = useState<SlotSymbol>(finalSymbol);
  const [spinning, setSpinning] = useState(false);

  useEffect(() => {
    setSpinning(true);
    let tick = Math.floor(Math.random() * SLOT_SYMBOLS.length);

    const interval = window.setInterval(() => {
      tick += 1;
      setSymbol(SLOT_SYMBOLS[tick % SLOT_SYMBOLS.length] ?? '🍒');
    }, 65);

    const stopTimer = window.setTimeout(() => {
      window.clearInterval(interval);
      setSymbol(finalSymbol);
      setSpinning(false);
    }, delayMs);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(stopTimer);
    };
  }, [spinKey, finalSymbol, delayMs]);

  return (
    <div
      className={
        spinning ? 'slot-machine__reel slot-machine__reel--spinning' : 'slot-machine__reel'
      }
    >
      <span className="slot-machine__symbol">{symbol}</span>
    </div>
  );
}

export function SlotMachineOverlay({
  costCoins,
  prizes,
  busy,
  result,
  error,
  onPlay,
  onClose,
}: SlotMachineOverlayProps): ReactElement {
  const [spinning, setSpinning] = useState(false);
  const [showWin, setShowWin] = useState(false);
  const [spinKey, setSpinKey] = useState<string | null>(null);
  const [displayReels, setDisplayReels] = useState<[SlotSymbol, SlotSymbol, SlotSymbol]>([
    '🍒',
    '🍋',
    '🔔',
  ]);

  useEffect(() => {
    if (!result?.reels || result.gameType !== 'SLOT') {
      return;
    }

    setDisplayReels(result.reels as [SlotSymbol, SlotSymbol, SlotSymbol]);
    setShowWin(false);
    setSpinning(true);
    setSpinKey(result.tip.id);

    const doneAt = (SLOT_STOP_DELAYS_MS[2] ?? 2700) + 120;
    const timer = window.setTimeout(() => {
      setSpinning(false);
      setShowWin(true);
    }, doneAt);

    return () => {
      window.clearTimeout(timer);
    };
  }, [result]);

  const matchLabel =
    result?.matchKind === 'JACKPOT'
      ? t('common.show.slotMatchJackpot')
      : result?.matchKind === 'THREE'
        ? t('common.show.slotMatchTriple')
        : result?.matchKind === 'PAIR'
          ? t('common.show.slotMatchPair')
          : result?.matchKind === 'NONE'
            ? t('common.show.slotMatchNone')
            : null;

  return (
    <div className="room-game-overlay" role="dialog" aria-modal="true" aria-labelledby="slot-title">
      <div className="room-game-overlay__panel">
        <header className="room-game-overlay__header">
          <h2 id="slot-title">{t('common.show.slotTitle')}</h2>
          <button type="button" className="room-game-overlay__close" onClick={onClose}>
            {t('common.show.gamesMenuClose')}
          </button>
        </header>
        <p className="room-game-overlay__hint">{t('common.show.slotHint')}</p>

        <div className="slot-machine">
          {displayReels.map((symbol, index) =>
            spinKey ? (
              <SlotReel
                key={`${spinKey}-${String(index)}`}
                finalSymbol={symbol}
                delayMs={SLOT_STOP_DELAYS_MS[index] ?? 1000}
                spinKey={spinKey}
              />
            ) : (
              <div key={`idle-${String(index)}`} className="slot-machine__reel">
                <span className="slot-machine__symbol">{symbol}</span>
              </div>
            ),
          )}
        </div>

        {showWin && result ? (
          <p className="room-game-overlay__result" role="status">
            {matchLabel ? `${matchLabel} · ` : ''}
            {t('common.show.gameWin').replace('{{prize}}', result.prize.label)}
          </p>
        ) : null}
        {error ? <p className="fm-error">{error}</p> : null}

        <div className="room-game-overlay__actions">
          <Button
            label={t('common.show.slotPlay').replace('{{cost}}', String(costCoins))}
            disabled={busy || spinning || prizes.length === 0}
            onClick={onPlay}
          />
        </div>

        <p className="room-game-overlay__paytable-title">{t('common.show.slotPaytable')}</p>
        <ul className="room-game-overlay__odds">
          {SLOT_PAYTABLE.map((row) => (
            <li key={row.kind}>
              <span>{t(row.labelKey)}</span>
              <span>{`${String(slotMatchOddsPercent(row.kind))}%`}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export type ActiveRoomGame = RoomGameType | null;
